// SPDX-License-Identifier: EUPL-1.2
// SPDX-FileCopyrightText: (c) c3tactile
import WebGL from "three/addons/capabilities/WebGL.js";
import { Viewer } from "./viewer.js";
import Liblouis from "liblouis-js-build";

window.VIEWER = {};

if (!(window.File && window.FileReader && window.FileList && window.Blob)) {
    console.error('The File APIs are not fully supported in this browser.');
} else if (!WebGL.isWebGLAvailable()) {
    console.error('WebGL is not supported in this browser.');
}

class App {
    /**
     * @param  {Element} el
     * @param  {Location} location
     * @param  {Liblouis} louis
     */
    constructor(el, location, louis) {
        const hash = location.hash ? location.hash.slice(1) : "";
        const hashParams = new URLSearchParams(hash);
        this.options = {
            kiosk: Boolean(hashParams.get("kiosk")),
            model: hashParams.get("model") || '',
            preset: hashParams.get("preset") || '',
            cameraPosition: hashParams.has("cameraPosition") ? hashParams.get("cameraPosition").split(',').map(Number) : null,
        };

        this.el = el;
        this.louis = louis;
        this.viewer = null;
        this.viewerEl = null;
        this.spinnerEl = el.querySelector('.spinner');
        this.form = document.getElementById('editor.form')

        this.api_url = new URL(document.URL).origin + '/api'

        this.hideSpinner();

        const options = this.options;

        if (options.kiosk) {
            const headerEl = document.querySelector('header');
            headerEl.style.display = 'none';
        }

        if (options.model) {
            this.view(options.model, '', new Map());
        }

    }

    /**
     * Sets up the view manager.
     * @return {Viewer}
     */
    createViewer() {
        this.viewerEl = document.getElementById('preview.threejs')
        this.viewer = new Viewer(this.viewerEl, this.options);
        return this.viewer;
    }

    preview_model() {
        this.showSpinner()
        this.disableButtons()
        let api_path = this.api_url + '/render'
        var data = {}
        new FormData(this.form).forEach((value, key) => data[key] = value);
        data["type"] = "glb"
        let requestOptions = {
            method: 'POST', headers: {
                'Content-Type': 'application/json',
            }, body: JSON.stringify(data),
        };

        fetch(api_path, requestOptions)
            .then(async response => {
                let r = await response.json()
                if (!response.ok) {
                    let message = r['code'] + ": " + r['message'] + "\n"
                    if ("traceback" in r) {
                        message += r["traceback"]
                    }
                    alert(message)
                }
                return r;
            })
            .then(data => {
                var fileMap = new Map()
                var arr = data['result'].split(','), mime = arr[0].match(/:(.*?);/)[1],
                    bstr = atob(arr[arr.length - 1]), n = bstr.length, u8arr = new Uint8Array(n);
                while (n--) {
                    u8arr[n] = bstr.charCodeAt(n);
                }
                fileMap.set('sign', new File([u8arr], 'sign.glb', {type: mime}))
                this.load(fileMap)
            }).finally(() => {
            this.hideSpinner();
            this.enableButtons();
        })
    }

    save_model() {
        this.disableButtons()
        let api_path = this.api_url + '/render'
        var form_data = {}
        new FormData(this.form).forEach((value, key) => form_data[key] = value);
        form_data["type"] = "stl"
        form_data["fast"] = false
        let requestOptions = {
            method: 'POST', headers: {
                'Content-Type': 'application/json',
            }, body: JSON.stringify(form_data),
        };

        fetch(api_path, requestOptions)
            .then(response => {
                if (!response.ok) {
                    let r = response.json()
                    let message = r['code'] + ": " + r['message'] + "\n"
                    if ("traceback" in r) {
                        message += r["traceback"]
                    }
                    alert(message)
                }
                return response.json();
            })
            .then(data => {
                var fileMap = new Map()
                var arr = data['result'].split(','), mime = arr[0].match(/:(.*?);/)[1],
                    bstr = atob(arr[arr.length - 1]), n = bstr.length, u8arr = new Uint8Array(n);
                while (n--) {
                    u8arr[n] = bstr.charCodeAt(n);
                }
                this.base64ToFile(u8arr, mime, form_data['text'].split(' ')[0] + '.stl')
            }).finally(() => {
            this.enableButtons()
        })
    }

    /**
     * Loads a fileset provided by user action.
     * @param  {Map<string, File>} fileMap
     */
    load(fileMap) {
        let rootFile;
        let rootPath;
        Array.from(fileMap).forEach(([path, file]) => {
            if (file.name.match(/\.(gltf|glb)$/)) {
                rootFile = file;
                rootPath = path.replace(file.name, '');
            }
        });

        if (!rootFile) {
            this.onError('No .gltf or .glb asset found.');
        }

        this.view(rootFile, rootPath, fileMap);
    }

    /**
     * Passes a model to the viewer, given file and resources.
     * @param  {File|string} rootFile
     * @param  {string} rootPath
     * @param  {Map<string, File>} fileMap
     */
    view(rootFile, rootPath, fileMap) {
        if (this.viewer) this.viewer.clear();

        const viewer = this.viewer || this.createViewer();

        const fileURL = typeof rootFile === 'string' ? rootFile : URL.createObjectURL(rootFile);

        const cleanup = () => {
            this.hideSpinner();
            if (typeof rootFile === 'object') URL.revokeObjectURL(fileURL);
        };

        viewer
            .load(fileURL, rootPath, fileMap)
            .catch((e) => this.onError(e))
            .then((gltf) => {
                cleanup();
            });
    }

    /**
     * @param  {Error} error
     */
    onError(error) {
        let message = (error || {}).message || error.toString();
        if (message.match(/ProgressEvent/)) {
            message = 'Unable to retrieve this file. Check JS console and browser network tab.';
        } else if (message.match(/Unexpected token/)) {
            message = `Unable to parse file content. Verify that this file is valid. Error: "${message}"`;
        } else if (error && error.target && error.target instanceof Image) {
            message = 'Missing texture: ' + error.target.src.split('/').pop();
        }
        window.alert(message);
        console.error(error);
    }

    showSpinner() {
        this.spinnerEl.style.display = 'block';
    }

    hideSpinner() {
        this.spinnerEl.style.display = 'none';
    }

    disableButtons() {
        document.getElementById("editor.button.preview").disabled = true
        document.getElementById("editor.button.save").disabled = true
    }

    enableButtons() {
        document.getElementById("editor.button.preview").disabled = false
        document.getElementById("editor.button.save").disabled = false
    }

    base64ToFile(base64, mimeType, fileName) {
        const blob = new Blob([base64], {type: mimeType});
        const url = URL.createObjectURL(blob);

        // Create a link element to download the file
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();

        // Cleanup
        URL.revokeObjectURL(url);
    }

    convert_braille() {
        const text = document.getElementById("editor.text").value.split("\n");
        const braille_txt = text.map(line => this.louis.translateString(
            "tables/de-g0.utb",
            line,
        )).join("\n");
        
        document.getElementById("editor.braille-txt").value = braille_txt;

        const braille = text.map(line => this.louis.translateString(
            "tables/unicode.dis,tables/de-g0.utb",
            line,
        )).join("\n");
        
        document.getElementById("editor.braille").value = braille;
    }

    validate_form(){
        let form_data = {}
        new FormData(this.form).forEach((value, key) => form_data[key] = value);
        if(form_data['text'].toUpperCase() != form_data['text']){
            alert("Profilschrift sollte nur DRUCKSCHRIFT enthalten")
        }
    }

}

document.addEventListener("DOMContentLoaded", async () => {
    const louis = await Liblouis.initLiblouis();
    await louis.loadTable("tables/unicode.dis,tables/de-g0.utb");

    const app = new App(document.getElementById("preview"), location, louis);
    window.VIEWER.app = app;
    console.info('[glTF Viewer] Debugging data exported as `window.VIEWER`.');
    window.VIEWER.app.createViewer()
});
